import { API_INDICATOR } from "@/constants/config"
import request from "@/http"
import { downloadFile } from "@/util/util"
import type { ResultVO } from "@/api"
import qs from "qs"
// ==============================================
const baseURL = API_INDICATOR
// ==============================================
// 网点筛选条件
export interface BranchFilters {
  // 层级关系
  outletscode?: string
  // 网点类型
  outletstype?: string
  // 网点定位
  outletscategory?: string
  // 日期（日期不传默认查最新一天）
  stadate?: string
}

interface RelationRankArgs {
  citycode: string
  brandcode: string
}

export interface RelationRankData {
  code: string
  name: string
  level: string
  longitude: number
  latitude: number
  cityId?: string
  secondCode?: string
  children?: RelationRankData[]
}

interface RelationRankResult {
  code: number
  msg: string
  data: RelationRankData[]
}

/**
 * 银行网点层级查询接口
 */
export async function getBranchRelationRank(params: RelationRankArgs): Promise<RelationRankResult> {
  try {
    const result = await request.post<RelationRankResult>(
      // `${baseURL}/v1/relation/rank`
      `/v1/industry/stores/relationLevel`,
      params,
      {
        headers: {
          withCredentials: false,
        },
      },
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

interface AnalysisZoneArgs {
  citycode: string
  brandcode: string
  outletscode?: string
  outletstype?: string
  outletscategory?: string
}

interface AnalysisZoneData {
  name: string
  address: string
  province: string
  city: string
  district: string
  staDate: string
  level: string

  // 客户经理数
  amAmounts: number
  /**个人客户渗透率*/
  icPenetration: number
  /**共有客户渗透率*/
  ccPenetration: number

  /**对公客户数*/
  corpCustomer: number
  /**比上月末对公客户数增长百分比*/
  corpCustomerPer: number

  /**核心网点类型数量*/
  coreTypeAmou: number
  /**骨干网点类型数量*/
  baboTypeAmou: number
  /**轻型网点类型数量*/
  lightTypeAmou: number
  /**龙头型经营类别网点数量*/
  faucetCategory: number
  /**市场型经营类别网点数量*/
  marketCategory: number
  /**社区型经营类别网点数量*/
  communityCategory: number
  /**乡镇型经营类别网点数量*/
  townshipCategory: number
  /**园区型经营类别网点数量*/
  parkCategory: number
  /**商圈型经营类别网点数量*/
  businessCategory: number
  /**辖区内智慧网点数量*/
  inteliAmounts: number
  /**辖区内单高柜网点数量*/
  shcoAmounts: number

  /**有贷企业数*/
  loansEnterAmount: number
  /**比上月末有贷企业数增长百分比*/
  loansEnterAmountPer: number

  /**代发工资*/
  payrollAgency: number
  /**比上月末代发工资增长百分比*/
  payrollAgencyPer: number

  /**企业掌银客户数*/
  mbank: number
  /**比上月末企业掌银客户数增长百分比*/
  mbankPer: number

  /**企业网银客户数*/
  ebank: number
  /**比上月末企业网银客户数增长百分比*/
  ebankPer: number

  /**个人有效客户数*/
  iecAmount: number
  /**比上月末个人有效客户数增长百分比*/
  iecAmountPer: number

  /**贵宾客户数*/
  vips: number
  /**比上月末贵宾客户数增长百分比*/
  vipsPer: number

  /**老年客户数*/
  olds: number
  /**比上月末老年客户数增长百分比*/
  oldsPer: number

  /**近10天日均到场客户数*/
  recTenAmounts: number
  /**比上月末近10天日均到场客户数增长百分比*/
  recTenAmountsPer: number

  /**掌银注册客户数*/
  wapZc: number
  /**比上月末掌银注册客户数增长百分比*/
  wapZcPer: number

  /**掌银月活客户数*/
  wapYh: number
  /**比上月末掌银月活客户数增长百分比*/
  wapYhPer: number

  /**微银行绑卡客户数*/
  wbnk: number
  /**比上月末绑卡客户数增长百分比*/
  wbnkPer: number
}

interface AnalysisZoneResult {
  code: number
  msg: string
  data: AnalysisZoneData[]
}

/**
 * 通过城市代码查询绩效指标
 */
export async function getAnalysisZone(params: AnalysisZoneArgs): Promise<AnalysisZoneResult> {
  try {
    const result = await request.post<AnalysisZoneResult>(`${baseURL}/v1/analysis/zone`, params, {
      headers: {
        withCredentials: false,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

type BusinessIndicatorsArgs = {
  citycode?: string
  brandcode: string
  ordertype: string
  isasc: boolean
  limit?: number
  offset?: number
} & BranchFilters

export interface BusinessIndicatorsData {
  name: string
  address: string
  province: string
  city: string
  district: string
  level: string
  totalPages?: number
  staDate?: string
  poiId: string

  /**此处的经纬度为保留字段 视清洗数据准确度看是否可用*/
  lon: number
  lat: number

  /**网点类型*/
  type: string

  /**经营分类*/
  category: string
  outletscode: string

  /**日均存款*/
  addAmounts: number
  /**上月末日均存款*/
  addAmountsLm: number
  /**年初日均存款*/
  addAmountsBy: number

  /**各项贷款*/
  adlAmounts: number
  /**上月末各项贷款*/
  adlAmountsLm: number
  /**年初各项贷款*/
  adlAmountsBy: number

  /**拨备前利润*/
  pbfAmounts: number
  /**上月末拨备前利润*/
  pbfAmountsLm: number
  /**年初拨备前利润*/
  pbfAmountsBy: number

  /**中间业务收入*/
  midIncome: number
  /**上月末中间业务收入*/
  midIncomeLm: number
  /**年初中间业务收入*/
  midIncomeBy: number

  /**网点负责人*/
  charger: string
  /**负责人职位*/
  chargerPosition: string

  /**日均存款一级支行排名*/
  addAmountsFir: number
  /**日均存款二级分行排名*/
  addAmountsSec: number
  /**日均存款一级分行排名*/
  addAmountsThi: number

  /**各项贷款一级支行排名*/
  adlAmountsFir: number
  /**各项贷款二级分行排名*/
  adlAmountsSec: number
  /**各项贷款一级分行排名*/
  adlAmountsThi: number

  /**一级支行网点拨备前利润排名*/
  pbfAmountsFir: number
  /**二级分行网点拨备前利润排名*/
  pbfAmountsSec: number
  /**一级分行网点拨备前利润排名*/
  pbfAmountsThi: number

  /**一级支行网点中间收入排名*/
  midIncomeFir: number
  /**二级分行网点中间输入排名*/
  midIncomeSec: number
  /**一级分行网点中间收入排名*/
  midIncomeThi: number
  /**所属一级支行下辖网点数量*/
  subBranchOA: number
  /**所属二级分行下辖网点数量*/
  secBranchOA: number
  /**所属一级分行下辖网点数量*/
  firBranchOA: number
}

interface BusinessIndicatorsResult {
  code: number
  msg: string
  data: BusinessIndicatorsData[]
}

/**
 * 查询经营指标
 */
export async function getBusinessIndicators(
  params: BusinessIndicatorsArgs,
): Promise<BusinessIndicatorsResult> {
  try {
    const { limit, offset, ...others } = params
    let str = qs.stringify({ limit, offset })
    str = str ? `?${str}` : str
    const result = await request.post<BusinessIndicatorsResult>(
      `${baseURL}/v1/kpi/zone${str}`,
      others,
      {
        headers: {
          withCredentials: false,
        },
      },
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface ProductIndicatorsData {
  name: string
  address: string
  province: string
  city: string
  district: string
  level: string
  totalPages?: number
  staDate?: string
  poiId: string

  /**此处的经纬度为保留字段 视清洗数据准确度看是否可用*/
  lon: number
  lat: number

  /**网点类型*/
  type: string

  /**经营分类*/
  category: string
  outletscode: string

  /**大额存单销售业绩*/
  codAmounts: number
  /**上月末大额存单销售业绩*/
  codAmountsLm: number
  /**年初大额存单销售业绩*/
  codAmountsBy: number

  /**基金销售业绩*/
  fund: number
  /**上月末基金销售业绩*/
  fundLm: number
  /**年初基金销售业绩*/
  fundBy: number

  /**理财销售业绩*/
  cftAmounts: number
  /**上月末理财销售业绩*/
  cftAmountsLm: number
  /**年初理财销售业绩*/
  cftAmountsBy: number

  /**网点负责人*/
  charger: string
  /**负责人职位*/
  chargerPosition: string

  /**'大额存单销售业绩一级支行排名'*/
  codAmountsFir: number
  /**'大额存单销售业绩二级分行排名'*/
  codAmountsSec: number
  /**'大额存单销售业绩一级分行排名'*/
  codAmountsThi: number

  /**'基金销售业绩一级支行排名'*/
  fundFir: number
  /**'基金销售业绩二级分行排名'*/
  fundSec: number
  /**'基金销售业绩省分行排名'*/
  fundThi: number

  /**'理财销售业绩一级支行排名'*/
  cftAmountsFir: number
  /**'理财销售业绩二级分行排名'*/
  cftAmountsSec: number
  /**'理财销售业绩省分行排名'*/
  cftAmountsThi: number
  /**所属一级支行下辖网点数量*/
  subBranchOA: number
  /**所属二级分行下辖网点数量*/
  secBranchOA: number
  /**所属一级分行下辖网点数量*/
  firBranchOA: number
}

interface ProductIndicatorsResult {
  code: number
  msg: string
  data: ProductIndicatorsData[]
}

/**
 * 查询产品指标
 */
export async function getProductIndicators(
  params: BusinessIndicatorsArgs,
): Promise<ProductIndicatorsResult> {
  try {
    const { limit, offset, ...others } = params
    let str = qs.stringify({ limit, offset })
    str = str ? `?${str}` : str
    const result = await request.post<ProductIndicatorsResult>(
      `${baseURL}/v1/index/product/zone${str}`,
      others,
      {
        headers: {
          withCredentials: false,
        },
      },
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface PublicIndicatorsData {
  name: string
  address: string
  city: string
  district: string
  type: string
  category: string
  outletscode: string
  totalPages?: number
  staDate?: string
  poiId: string

  /**对公客户数*/
  corpCustomer: number
  /**上月末对公客户数*/
  corpCustomerLm: number
  /**年初对公客户数*/
  corpCustomerBy: number

  /**有贷企业数*/
  loansEnterAmount: number
  /**上月末有贷企业数*/
  loansEnterAmountLm: number
  /**年初有贷企业数*/
  loansEnterAmountBy: number

  /**代发工资*/
  payrollAgency: number
  /**上月末代发工资*/
  payrollAgencyLm: number
  /**年初代发工资*/
  payrollAgencyBy: number

  /**企业掌银客户数*/
  mbank: number
  /**上月末企业掌银客户数*/
  mbankLm: number
  /**年初企业掌银客户数*/
  mbankBy: number

  /**企业网银客户数*/
  ebank: number
  /**上月末企业网银客户数*/
  ebankLm: number
  /**年初企业网银客户数*/
  ebankBy: number

  /**负责人*/
  charger: string
  /**负责人职位*/
  chargerPosition: string

  /**一级支行对公客户数排名*/
  corpCustomerFir: number
  /**二级分行对公客户数排名*/
  corpCustomerSec: number
  /**一级分行对公客户数排名*/
  corpCustomerThi: number

  /**一级支行有贷企业数排名*/
  loansEnterAmountFir: number
  /**二级分行有贷企业数排名*/
  loansEnterAmountSec: number
  /**一级分行有贷企业数排名*/
  loansEnterAmountThi: number

  /**一级支行代发工资排名*/
  payrollAgencyFir: number
  /**'二级分行代发工资排名'*/
  payrollAencySec: number
  /**'一级分行代发工资排名'*/
  payrollAgencyThi: number

  /**'一级支行企业掌印排名'*/
  mbankFir: number
  /**'二级分行企业掌印排名'*/
  mbankSec: number
  /**'一级分行企业掌印排名'*/
  mbankThi: number

  /**'一级支行企业网银排名'*/
  ebankFir: number
  /**'二级分行企业网银排名'*/
  ebankSec: number
  /**'一级分行企业网银排名'*/
  ebankThi: number
  /**所属一级支行下辖网点数量*/
  subBranchOA: number
  /**所属二级分行下辖网点数量*/
  secBranchOA: number
  /**所属一级分行下辖网点数量*/
  firBranchOA: number
}

interface PublicIndicatorsResult {
  code: number
  msg: string
  data: PublicIndicatorsData[]
}

/**
 * 查询对公客户指标
 */
export async function getPublicIndicators(
  params: BusinessIndicatorsArgs,
): Promise<PublicIndicatorsResult> {
  try {
    const { limit, offset, ...others } = params
    let str = qs.stringify({ limit, offset })
    str = str ? `?${str}` : str
    const result = await request.post<PublicIndicatorsResult>(
      `${baseURL}/v1/customer/company/zone${str}`,
      others,
      {
        headers: {
          withCredentials: false,
        },
      },
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

export interface PersonalIndicatorsData {
  name: string
  address: string
  city: string
  district: string
  type: string
  category: string
  outletscode: string
  totalPages?: number
  staDate?: string
  poiId: string

  /**个人有效客户数*/
  iecAmount: number
  /**上月末个人有效客户数*/
  iecAmountLm: number
  /**年初个人有效客户数*/
  iecAmountBy: number

  /**贵宾客户数*/
  vips: number
  /**上月末贵宾客户数*/
  vipsLm: number
  /**年初贵宾客户数*/
  vipsBy: number

  /**老年客户数*/
  olds: number
  /**上月末老年客户数*/
  oldsLm: number
  /**年初老年客户数*/
  oldsBy: number

  /**近10天日均到场客户数*/
  recTenAmounts: number
  /**上月末近10天日均到场客户数*/
  recTenAmountsLm: number
  /**年初近10天日均到场客户数*/
  recTenAmountsBy: number

  /**掌银注册客户数*/
  wapZc: number
  /**上月末掌银注册客户数*/
  wapZcLm: number
  /**年初掌银注册客户数*/
  wapZcBy: number

  /**掌银月活客户数*/
  wapYh: number
  /**上月末掌银月活客户数*/
  wapYhLm: number
  /**年初掌银月活客户数*/
  wapYhBy: number

  /**微银行绑卡客户数*/
  wbnk: number
  /**上月末微银行绑卡客户数*/
  wbnkLm: number
  /**年初微银行绑卡客户数*/
  wbnkBy: number

  /**网点负责人*/
  charger: string
  /**负责人职位*/
  chargerPosition: string

  /**'一级支行网点个人有效客户数排名'*/
  iecAmountFir: number
  /**'二级分行网点个人有效客户数排名'*/
  iecAmountSec: number
  /**'一级分行网点个人有效客户数排名'*/
  iecAmountThi: number

  /**'一级支行网点贵宾客户数排名'*/
  vipsFir: number
  /**'二级分行网点贵宾客户数排名'*/
  vipsSec: number
  /**'一级分行网点贵宾客户数排名'*/
  vipsThi: number

  /**'一级支行网点老年客户数排名'*/
  oldsFir: number
  /**'二级分行网点老年客户数排名'*/
  oldsSec: number
  /**'一级分行网点老年客户数排名'*/
  oldsThi: number

  /**'一级支行网点近10天日均到场客户数排名'*/
  recTenAmountsFir: number
  /**'二级分行网点近10天日均到场客户数排名'*/
  recTenAmountsSec: number
  /**'一级分行网点近10天日均到场客户数排名'*/
  recTenAmountsThi: number

  /**'一级支行网点掌银注册客户数排名'*/
  wapZcFir: number
  /**'二级分行网点掌银注册客户数排名'*/
  wapZcSec: number
  /**'一级分行网点掌银注册客户数排名'*/
  wapZcThi: number

  /**'一级支行网点掌银月活客户数排名'*/
  wapYhFir: number
  /**'二级分行网点掌银月活客户数排名'*/
  wapYhSec: number
  /**'一级分行网点掌银月活客户数排名'*/
  wapYhThi: number

  /**'一级支行网点微银行绑卡客户数排名'*/
  wbnkFir: number
  /**'二级分行网点微银行绑卡客户数排名'*/
  wbnkSec: number
  /**'一级分行网点微银行绑卡客户数排名'*/
  wbnkThi: number
  /**所属一级支行下辖网点数量*/
  subBranchOA: number
  /**所属二级分行下辖网点数量*/
  secBranchOA: number
  /**所属一级分行下辖网点数量*/
  firBranchOA: number
}

interface PersonalIndicatorsResult {
  code: number
  msg: string
  data: PersonalIndicatorsData[]
}

/**
 * 查询个人客户指标
 */
export async function getPersonalIndicators(
  params: BusinessIndicatorsArgs,
): Promise<PersonalIndicatorsResult> {
  try {
    const { limit, offset, ...others } = params
    let str = qs.stringify({ limit, offset })
    str = str ? `?${str}` : str
    const result = await request.post<PersonalIndicatorsResult>(
      `${baseURL}/v1/customer/person/zone${str}`,
      others,
      {
        headers: {
          withCredentials: false,
        },
      },
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

interface StaDateResult {
  code: number
  msg: string
  data: string
}

/**
 * 获取数据最新一天日期
 */
export async function getOutletsStaDate(): Promise<StaDateResult> {
  try {
    const result = await request.get(`${baseURL}/v1/outlets/staDate`)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface GeocodeAreaResult {
  cityCode: string
  cityName: string
  districtCode: string
  districtName: string
  provinceCode: string
  provinceName: string
}

/**
 * 根据经纬度获取省市区数据
 */
export async function getGeocodeAreaByLocation(location: number[]): Promise<GeocodeAreaResult> {
  try {
    const [longitude, latitude] = location
    const result = await request.get(`/v1/geocode/area`, {
      params: {
        location: `${longitude},${latitude}`,
      },
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

/**
 * 获取操作指引文件
 */
export async function getOperationGuide() {
  try {
    const result = await request.get<any>("/v1/manual", {
      responseType: "blob",
    })
    downloadFile(result.data, `操作提示.doc`)
    return result.status
  } catch (error) {
    throw error
  }
}

interface PoiLocationDTO {
  // 省
  province: string
  provinceCode: string
  // 城市
  city: string
  cityCode: string
  // 区县
  district: string
  districtCode: string
  // 地址
  address: string
}

interface PoiManageDTO {
  // 主管人姓名
  charger: string
  // 联系电话
  phone: string
}

interface PoiClassifyDTO {
  // 网点类型
  outletstype: string
  // 网点定位
  outletscategory: string
  // 是否是智慧网点
  isInteli: boolean
}

export interface PoiOrgDTO {
  // 一级分行/省分行
  firstBranch: string
  firstBranchCode: string
  // 二级分行
  secondBranch: string
  secondBranchCode: string
  // 一级支行
  firstSubbranch: string
  firstSubbranchCode: string
  // 二级支行
  name: string
  number: string
}

export interface OutletsImgDTO {
  // 图片ID
  id?: number
  // 图片名称
  name: string
  // 上传路径
  uploadPath: string
  // 所属网点编码
  outletsCode: string
  // 上传时间 yyyy-MM-dd HH:mm:ss
  uploadTime: string
}

interface PoiEquipmentDTO {
  // 超级柜台
  superCounter: number
  // ATM
  atm: number
  // 自助服务终端
  selfserviceTerminal: number
}

interface PoiCounterDTO {
  // 高柜柜口
  highCounter: number
  // 低柜柜口
  lowCounter: number
}

export interface StoreDetailResult {
  // 网点位置
  poiLocation: PoiLocationDTO
  // 网点负责
  poiManage: PoiManageDTO
  // 网点分类
  poiClassify: PoiClassifyDTO
  // 网点管辖行
  poiOrg: PoiOrgDTO
  // 网点实茂图集
  poiImages: OutletsImgDTO[]
  // 网点设备分析
  poiEquipment: PoiEquipmentDTO
  // 网点柜口分析
  poiCounter: PoiCounterDTO
}

/**
 * 获取网点概览详情
 */
export async function getStoreDetail(outletscode: string) {
  try {
    const result = await request.post<StoreDetailResult>("/v1/industry/stores/detail", {
      id: outletscode,
    })
    return result?.data
  } catch (error) {
    throw error
  }
}

interface PoiDetailQueryArg {
  // 点位ID
  id: string
  // 网点位置
  poiLocation: PoiLocationDTO
  // 网点负责
  poiManage: PoiManageDTO
  // 网点分类
  poiClassify: PoiClassifyDTO
  // 网点实茂图集
  poiImages: OutletsImgDTO[]
  // 网点设备分析
  poiEquipment: PoiEquipmentDTO
  // 网点柜口分析
  poiCounter: PoiCounterDTO
}

/**
 * 设置网点概览
 */
export async function changeStoreDetail(params: PoiDetailQueryArg) {
  try {
    const result = await request.put<ResultVO>("/v1/industry/stores/detail", params)
    return result?.data
  } catch (error) {
    throw error
  }
}

interface UploadImageResult {
  code: number
  mesg: string
  data: OutletsImgDTO
}

/**
 * 上传网点图片
 */
export async function uploadStoreImage(params: any) {
  try {
    const outletscode = params.get("outletscode") || ""
    const result = await request.put<UploadImageResult>(
      `/v1/industry/stores/uploadImage/${outletscode}`,
      params,
    )
    return result?.data
  } catch (error) {
    throw error
  }
}

/**
 * 删除网点图片
 */
export async function deleteStoreImage(imageId: number) {
  try {
    const result = await request.delete<ResultVO>(`/v1/industry/stores/deleteImage/${imageId}`)
    return result?.data
  } catch (error) {
    throw error
  }
}
